import { create } from 'zustand';
import type { Product } from '../../catalog/domain-api'
import type { ProductLookupResult } from '../../catalog/domain-api'
import type { BulkPreviewItem as InventoryBulkPreviewRow } from '../../catalog/domain-api';
import type {
  StockMovementReasonKey,
  StockMovementRow,
  StockRow,
  TransferRow,
} from '../../inventory/stock/public-api';
import {
  DEFAULT_BULK_CATEGORY_SCOPE,
  DEFAULT_BULK_PRICE_ROUNDING,
  type BulkCategoryScope,
  type BulkPriceRounding,
} from '../../catalog/public-api';
import type { PhysicalCountRow } from '../application/inventoryOperations.types';

export type InventoryEntrySuggestion = Extract<ProductLookupResult, { kind: 'remote_hit' }> | null;

export type { PhysicalCountRow };

type InventoryUiState = {
  transferModalVisible: boolean;
  transferPickerVisible: boolean;
  transferPickerSearch: string;
  selectedStock: StockRow | null;
  destinationBranchId: string | null;
  transferQuantity: string;
  transferWaitForReceive: boolean;
  isSubmittingTransfer: boolean;
  pendingTransfers: TransferRow[];
  pendingTransfersLoading: boolean;
  processingTransferId: string | null;

  adjustModalVisible: boolean;
  adjustStockRow: StockRow | null;
  adjustDelta: string;
  adjustReason: StockMovementReasonKey;
  adjustSaving: boolean;

  costCorrectModalVisible: boolean;
  costCorrectStockRow: StockRow | null;
  costCorrectInput: string;
  costCorrectSaving: boolean;

  addStockVisible: boolean;
  productQuery: string;
  productQueryDebounced: string;
  productResults: Product[];
  productPageOffset: number;
  productHasMore: boolean;
  productLoadingMore: boolean;
  selectedProduct: Product | null;
  entryQuantity: string;
  entryUnitCost: string;
  entryMinQuantity: string;
  entryExpiry: string;
  entryReason: 'purchase_reception' | 'stock_restock' | 'stock_initial_load';
  entrySupplierId: string | null;
  entryLotNumber: string;
  entryVariantId: string | null;
  savingEntry: boolean;
  entryScanFeedback: string | null;
  entrySuggestion: InventoryEntrySuggestion;
  lastUnitCost: number | null;

  historyVisible: boolean;
  historyLoading: boolean;
  movementHistory: StockMovementRow[];
  historyOffset: number;
  historyHasMore: boolean;
  historyLoadingMore: boolean;
  historySelectedStock: StockRow | null;

  bulkCoeffVisible: boolean;
  bulkCategoryScope: BulkCategoryScope;
  bulkSearch: string;
  bulkPercentInput: string;
  bulkApplyTo: 'prices' | 'quantities';
  bulkPriceRounding: BulkPriceRounding;
  bulkScannerVisible: boolean;
  bulkPreview: InventoryBulkPreviewRow[];
  bulkApplyTotal: number;
  bulkPreviewLoading: boolean;
  bulkSaving: boolean;
  bulkSaveProgress: { done: number; total: number } | null;

  physicalCountVisible: boolean;
  physicalCountRows: PhysicalCountRow[];
  physicalCountLoading: boolean;
  physicalCountSaving: boolean;

  activityModalVisible: boolean;
  pendingTransfersModalVisible: boolean;
  searchScannerVisible: boolean;
  entryScannerVisible: boolean;
  lookupFeedback: string | null;

  setTransferModalVisible: (v: boolean) => void;
  setTransferPickerVisible: (v: boolean) => void;
  setTransferPickerSearch: (s: string) => void;
  setSelectedStock: (r: StockRow | null) => void;
  setDestinationBranchId: (id: string | null) => void;
  setTransferQuantity: (s: string) => void;
  setTransferWaitForReceive: (v: boolean) => void;
  setIsSubmittingTransfer: (v: boolean) => void;
  setPendingTransfers: (t: TransferRow[] | ((prev: TransferRow[]) => TransferRow[])) => void;
  setPendingTransfersLoading: (v: boolean) => void;
  setProcessingTransferId: (id: string | null) => void;
  resetTransfer: () => void;

  setAdjustModalVisible: (v: boolean) => void;
  setAdjustStockRow: (r: StockRow | null) => void;
  setAdjustDelta: (s: string) => void;
  setAdjustReason: (r: StockMovementReasonKey) => void;
  setAdjustSaving: (v: boolean) => void;
  resetAdjustment: () => void;

  setCostCorrectModalVisible: (v: boolean) => void;
  setCostCorrectStockRow: (r: StockRow | null) => void;
  setCostCorrectInput: (s: string) => void;
  setCostCorrectSaving: (v: boolean) => void;
  resetCostCorrect: () => void;

  setAddStockVisible: (v: boolean) => void;
  setProductQuery: (s: string) => void;
  setProductQueryDebounced: (s: string) => void;
  setProductResults: (p: Product[] | ((prev: Product[]) => Product[])) => void;
  setProductPageOffset: (n: number | ((prev: number) => number)) => void;
  setProductHasMore: (v: boolean) => void;
  setProductLoadingMore: (v: boolean) => void;
  setSelectedProduct: (p: Product | null) => void;
  setEntryQuantity: (s: string) => void;
  setEntryUnitCost: (s: string) => void;
  setEntryMinQuantity: (s: string) => void;
  setEntryExpiry: (s: string) => void;
  setEntryReason: (r: InventoryUiState['entryReason']) => void;
  setEntrySupplierId: (id: string | null) => void;
  setEntryLotNumber: (s: string) => void;
  setEntryVariantId: (id: string | null) => void;
  setSavingEntry: (v: boolean) => void;
  setEntryScanFeedback: (s: string | null) => void;
  setEntrySuggestion: (s: InventoryEntrySuggestion) => void;
  setLastUnitCost: (c: number | null) => void;
  resetAddStock: () => void;

  setHistoryVisible: (v: boolean) => void;
  setHistoryLoading: (v: boolean) => void;
  setMovementHistory: (m: StockMovementRow[] | ((prev: StockMovementRow[]) => StockMovementRow[])) => void;
  setHistoryOffset: (n: number | ((prev: number) => number)) => void;
  setHistoryHasMore: (v: boolean) => void;
  setHistoryLoadingMore: (v: boolean) => void;
  setHistorySelectedStock: (s: StockRow | null) => void;
  resetHistory: () => void;

  setBulkCoeffVisible: (v: boolean) => void;
  setBulkCategoryScope: (scope: BulkCategoryScope) => void;
  setBulkApplyTotal: (n: number) => void;
  setBulkSaveProgress: (p: { done: number; total: number } | null) => void;
  setBulkSearch: (s: string) => void;
  setBulkPercentInput: (s: string) => void;
  setBulkApplyTo: (t: 'prices' | 'quantities') => void;
  setBulkPriceRounding: (r: BulkPriceRounding) => void;
  setBulkScannerVisible: (v: boolean) => void;
  setBulkPreview: (p: InventoryBulkPreviewRow[] | ((prev: InventoryBulkPreviewRow[]) => InventoryBulkPreviewRow[])) => void;
  setBulkPreviewLoading: (v: boolean) => void;
  setBulkSaving: (v: boolean) => void;
  resetBulk: () => void;

  setPhysicalCountVisible: (v: boolean) => void;
  setPhysicalCountRows: (r: PhysicalCountRow[] | ((prev: PhysicalCountRow[]) => PhysicalCountRow[])) => void;
  setPhysicalCountLoading: (v: boolean) => void;
  setPhysicalCountSaving: (v: boolean) => void;
  resetPhysicalCount: () => void;

  setActivityModalVisible: (v: boolean) => void;
  setPendingTransfersModalVisible: (v: boolean) => void;
  setSearchScannerVisible: (v: boolean) => void;
  setEntryScannerVisible: (v: boolean) => void;
  setLookupFeedback: (f: string | null) => void;
  resetModals: () => void;

  resetAll: () => void;

  /** Bumped when stock data changes from outside the Inventory screen (ej. purchase reception). */
  stockDataRevision: number;
  bumpStockDataRevision: () => void;

  /** #801: Productos expandidos en el listado de stock (expand/collapse variantes). */
  expandedStockProductIds: Set<string>;
  toggleStockProductExpanded: (productId: string) => void;
  clearExpandedStockProducts: () => void;
};

const initialTransfer = () => ({
  transferModalVisible: false,
  transferPickerVisible: false,
  transferPickerSearch: '',
  selectedStock: null as StockRow | null,
  destinationBranchId: null as string | null,
  transferQuantity: '1',
  transferWaitForReceive: false,
  isSubmittingTransfer: false,
  pendingTransfers: [] as TransferRow[],
  pendingTransfersLoading: false,
  processingTransferId: null as string | null,
});

const initialAdjustment = () => ({
  adjustModalVisible: false,
  adjustStockRow: null as StockRow | null,
  adjustDelta: '',
  adjustReason: 'shrinkage' as StockMovementReasonKey,
  adjustSaving: false,
});

const initialCostCorrect = () => ({
  costCorrectModalVisible: false,
  costCorrectStockRow: null as StockRow | null,
  costCorrectInput: '',
  costCorrectSaving: false,
});

const initialAddStock = () => ({
  addStockVisible: false,
  productQuery: '',
  productQueryDebounced: '',
  productResults: [] as Product[],
  productPageOffset: 0,
  productHasMore: false,
  productLoadingMore: false,
  selectedProduct: null as Product | null,
  entryQuantity: '',
  entryUnitCost: '',
  entryMinQuantity: '0',
  entryExpiry: '',
  entryReason: 'purchase_reception' as const,
  entrySupplierId: null as string | null,
  entryLotNumber: '',
  entryVariantId: null as string | null,
  savingEntry: false,
  entryScanFeedback: null as string | null,
  entrySuggestion: null as InventoryEntrySuggestion,
  lastUnitCost: null as number | null,
});

const initialHistory = () => ({
  historyVisible: false,
  historyLoading: false,
  movementHistory: [] as StockMovementRow[],
  historyOffset: 0,
  historyHasMore: false,
  historyLoadingMore: false,
  historySelectedStock: null as StockRow | null,
});

const initialBulk = () => ({
  bulkCoeffVisible: false,
  bulkCategoryScope: DEFAULT_BULK_CATEGORY_SCOPE,
  bulkSearch: '',
  bulkPercentInput: '5',
  bulkApplyTo: 'prices' as const,
  bulkPriceRounding: DEFAULT_BULK_PRICE_ROUNDING,
  bulkScannerVisible: false,
  bulkPreview: [] as InventoryBulkPreviewRow[],
  bulkApplyTotal: 0,
  bulkPreviewLoading: false,
  bulkSaving: false,
  bulkSaveProgress: null as { done: number; total: number } | null,
});

const initialPhysicalCount = () => ({
  physicalCountVisible: false,
  physicalCountRows: [] as PhysicalCountRow[],
  physicalCountLoading: false,
  physicalCountSaving: false,
});

const initialModals = () => ({
  activityModalVisible: false,
  pendingTransfersModalVisible: false,
  searchScannerVisible: false,
  entryScannerVisible: false,
  lookupFeedback: null as string | null,
});

const initialAll = () => ({
  ...initialTransfer(),
  ...initialAdjustment(),
  ...initialCostCorrect(),
  ...initialAddStock(),
  ...initialHistory(),
  ...initialBulk(),
  ...initialPhysicalCount(),
  ...initialModals(),
});

export const useInventoryUiStore = create<InventoryUiState>((set) => ({
  ...initialAll(),

  setTransferModalVisible: (v) => set({ transferModalVisible: v }),
  setTransferPickerVisible: (v) => set({ transferPickerVisible: v }),
  setTransferPickerSearch: (s) => set({ transferPickerSearch: s }),
  setSelectedStock: (r) => set({ selectedStock: r }),
  setDestinationBranchId: (id) => set({ destinationBranchId: id }),
  setTransferQuantity: (s) => set({ transferQuantity: s }),
  setTransferWaitForReceive: (v) => set({ transferWaitForReceive: v }),
  setIsSubmittingTransfer: (v) => set({ isSubmittingTransfer: v }),
  setPendingTransfers: (t) =>
    set((state) => ({
      pendingTransfers: typeof t === 'function' ? t(state.pendingTransfers) : t,
    })),
  setPendingTransfersLoading: (v) => set({ pendingTransfersLoading: v }),
  setProcessingTransferId: (id) => set({ processingTransferId: id }),
  resetTransfer: () => set(initialTransfer()),

  setAdjustModalVisible: (v) => set({ adjustModalVisible: v }),
  setAdjustStockRow: (r) => set({ adjustStockRow: r }),
  setAdjustDelta: (s) => set({ adjustDelta: s }),
  setAdjustReason: (r) => set({ adjustReason: r }),
  setAdjustSaving: (v) => set({ adjustSaving: v }),
  resetAdjustment: () => set(initialAdjustment()),

  setCostCorrectModalVisible: (v) => set({ costCorrectModalVisible: v }),
  setCostCorrectStockRow: (r) => set({ costCorrectStockRow: r }),
  setCostCorrectInput: (s) => set({ costCorrectInput: s }),
  setCostCorrectSaving: (v) => set({ costCorrectSaving: v }),
  resetCostCorrect: () => set(initialCostCorrect()),

  setAddStockVisible: (v) => set({ addStockVisible: v }),
  setProductQuery: (s) => set({ productQuery: s }),
  setProductQueryDebounced: (s) => set({ productQueryDebounced: s }),
  setProductResults: (p) =>
    set((state) => ({
      productResults: typeof p === 'function' ? p(state.productResults) : p,
    })),
  setProductPageOffset: (n) =>
    set((state) => ({
      productPageOffset: typeof n === 'function' ? n(state.productPageOffset) : n,
    })),
  setProductHasMore: (v) => set({ productHasMore: v }),
  setProductLoadingMore: (v) => set({ productLoadingMore: v }),
  setSelectedProduct: (p) => set({ selectedProduct: p }),
  setEntryQuantity: (s) => set({ entryQuantity: s }),
  setEntryUnitCost: (s) => set({ entryUnitCost: s }),
  setEntryMinQuantity: (s) => set({ entryMinQuantity: s }),
  setEntryExpiry: (s) => set({ entryExpiry: s }),
  setEntryReason: (r) => set({ entryReason: r }),
  setEntrySupplierId: (id) => set({ entrySupplierId: id }),
  setEntryLotNumber: (s) => set({ entryLotNumber: s }),
  setEntryVariantId: (id: string | null) => set({ entryVariantId: id }),
  setSavingEntry: (v) => set({ savingEntry: v }),
  setEntryScanFeedback: (s) => set({ entryScanFeedback: s }),
  setEntrySuggestion: (s) => set({ entrySuggestion: s }),
  setLastUnitCost: (c) => set({ lastUnitCost: c }),
  resetAddStock: () => set(initialAddStock()),

  setHistoryVisible: (v) => set({ historyVisible: v }),
  setHistoryLoading: (v) => set({ historyLoading: v }),
  setMovementHistory: (m) =>
    set((state) => ({
      movementHistory: typeof m === 'function' ? m(state.movementHistory) : m,
    })),
  setHistoryOffset: (n) =>
    set((state) => ({
      historyOffset: typeof n === 'function' ? n(state.historyOffset) : n,
    })),
  setHistoryHasMore: (v) => set({ historyHasMore: v }),
  setHistoryLoadingMore: (v) => set({ historyLoadingMore: v }),
  setHistorySelectedStock: (s) => set({ historySelectedStock: s }),
  resetHistory: () => set(initialHistory()),

  setBulkCoeffVisible: (v) => set({ bulkCoeffVisible: v }),
  setBulkCategoryScope: (scope) => set({ bulkCategoryScope: scope }),
  setBulkApplyTotal: (n) => set({ bulkApplyTotal: n }),
  setBulkSaveProgress: (p) => set({ bulkSaveProgress: p }),
  setBulkSearch: (s) => set({ bulkSearch: s }),
  setBulkPercentInput: (s) => set({ bulkPercentInput: s }),
  setBulkApplyTo: (t) =>
    set((state) => ({
      bulkApplyTo: t,
      bulkPriceRounding: t === 'prices' ? state.bulkPriceRounding : DEFAULT_BULK_PRICE_ROUNDING,
    })),
  setBulkPriceRounding: (r) => set({ bulkPriceRounding: r }),
  setBulkScannerVisible: (v) => set({ bulkScannerVisible: v }),
  setBulkPreview: (p) =>
    set((state) => ({
      bulkPreview: typeof p === 'function' ? p(state.bulkPreview) : p,
    })),
  setBulkPreviewLoading: (v) => set({ bulkPreviewLoading: v }),
  setBulkSaving: (v) => set({ bulkSaving: v }),
  resetBulk: () => set(initialBulk()),

  setPhysicalCountVisible: (v) => set({ physicalCountVisible: v }),
  setPhysicalCountRows: (r) =>
    set((state) => ({
      physicalCountRows: typeof r === 'function' ? r(state.physicalCountRows) : r,
    })),
  setPhysicalCountLoading: (v) => set({ physicalCountLoading: v }),
  setPhysicalCountSaving: (v) => set({ physicalCountSaving: v }),
  resetPhysicalCount: () => set(initialPhysicalCount()),

  setActivityModalVisible: (v) => set({ activityModalVisible: v }),
  setPendingTransfersModalVisible: (v) => set({ pendingTransfersModalVisible: v }),
  setSearchScannerVisible: (v) => set({ searchScannerVisible: v }),
  setEntryScannerVisible: (v) => set({ entryScannerVisible: v }),
  setLookupFeedback: (f) => set({ lookupFeedback: f }),
  resetModals: () => set(initialModals()),

  resetAll: () => set({ ...initialAll(), expandedStockProductIds: new Set<string>() }),

  stockDataRevision: 0,
  bumpStockDataRevision: () => set((s) => ({ stockDataRevision: s.stockDataRevision + 1 })),

  expandedStockProductIds: new Set<string>(),
  toggleStockProductExpanded: (productId) =>
    set((s) => {
      const next = new Set(s.expandedStockProductIds);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return { expandedStockProductIds: next };
    }),
  clearExpandedStockProducts: () => set({ expandedStockProductIds: new Set<string>() }),
}));
